<template>
  <button
    :class="[
      buttonClass,
      'font-weight-semibold',
      {
        'background-hover-trasparent': !hideBackgroundTransparenHover,
        'align-to-left-baseline': alignToLeftBaseline
      }
    ]"
    :data-test="dataTest"
    :disabled="disabled"
    class="btn tw-relative tw-px-2 tw-py-2"
    type="button"
    @click.stop="onClickButton">
    <div
      v-if="badgeContent"
      :class="badgeColor"
      class="btn-badge"
      data-test="btn-badge">
      {{ badgeContent }}
    </div>
    <div
      :class="[
        {['icon-position-' + iconPosition]: iconClass !== null},
        {'tw-flex tw-justify-center tw-items-center': !subLabel},
      ]"
      class="btn-content">
      <div
        v-if="loading"
        class="btn-loading-container">
        <Loading
          :revertColor="isSolid && !disabled"
          :template="template"
          dataTest="button-loading" />
      </div>
      <FontIcon
        v-if="iconClass !== null"
        :class="[iconPositionClass, {'h-25 w-25': isSvgSprite}]"
        :iconClass="iconClass"
        :isSvgSprite="isSvgSprite" />
      <span
        v-if="label"
        :class="[textTransformClass, {'text-nowrap': noWrap}]"
        class="tw-w-full overflow-hidden">{{ label }}</span>
      <div
        v-if="subLabel"
        class="small">
        {{ subLabel }}
      </div>
    </div>
  </button>
</template>

<script>
import FontIcon from 'frontcore/components/FontIcon.vue'
import Loading from 'frontcore/components/loadings/Loading.vue'

/**
 * @deprecated - Use CoreButton instead
 */
const component = {
  components: {
    FontIcon,
    Loading,
  },
  props: {
    label: {
      type: String,
    },
    loading: {
      type: Boolean,
    },
    textUppercase: {
      type: Boolean,
    },
    template: {
      type: String,
      default: 'default',
      validator (value) {
        const designSystemTemplates = [
          "danger",
          "default",
          "neutral",
          "success",
          "warning",
        ]
        if (!designSystemTemplates.includes(value)) {
          // eslint-disable-next-line no-console
          console.warn(`Template ${value} is not included in the design system, please change it for ${designSystemTemplates}`)
        }
        return true
      },
    },
    iconClass: {
      type: String,
      default: null,
    },
    iconPosition: {
      type: String,
      default: 'left',
      validator (position) {
        return position === 'left' || position === 'right'
      },
    },
    dataTest: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    onClickHandler: {
      type: Function,
      default: null,
    },
    noWrap: {
      type: Boolean,
    },
    badgeContent: {
      type: Number,
      default: 0,
    },
    badgeColor: {
      type: String,
      default: 'badge-yellow',
      validator (val) {
        return ['badge-yellow', 'badge-red', 'badge-green', 'badge-blue'].includes(val)
      },
    },
    outline: {
      type: Boolean,
    },
    flat: {
      type: Boolean,
    },
    subLabel: {
      type: String,
      required: false,
    },
    hideBackgroundTransparenHover: {
      type: Boolean,
      default: true,
    },
    isSvgSprite: {
      type: Boolean,
      default: false,
    },
    alignToLeftBaseline: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isSolid () {
      return !(this.flat || this.outline)
    },
    textTransformClass () {
      return this.textUppercase ? 'tw-uppercase' : 'tw-normal-case'
    },
    buttonClass () {
      if (this.outline) {
        return `btn-outline-${this.template}`
      } else if (this.flat) {
        return `btn-flat-${this.template}`
      }

      return `btn-${this.template}`
    },
    iconPositionClass () {
      if (!this.label) return ''
      return this.iconPosition === 'left' ? 'mr-2' : 'ml-2'
    },
  },
  created () {
    if (this.outline && this.flat) {
      throw new Error('Button can only be either outline or flat')
    }
  },
  methods: {
    onClickButton (e) {
      if (this.onClickHandler && !this.loading && !this.disabled) this.onClickHandler(e)
    },
  },
}

export default component
</script>

<style lang="scss">

@each $template, $mlui-color in $mlui-components-templates {
  $base: nth($mlui-color, 1);
  $light: nth($mlui-color, 2);
  $dark: nth($mlui-color, 3);
  $font-light: nth($mlui-color, 5);
  $font-dark: nth($mlui-color, 6);
  .btn-#{$template} {
    background-color: $base !important;
    color: $text-white !important;
    border: 0.06rem solid $base !important;
  }
  .btn-#{$template}:not(.btn:disabled):hover {
    background-color: $dark !important;
    color: $text-white !important;
    border: 0.06rem solid $dark !important;
  }
  .btn-outline-#{$template} {
    background-color: $background-white !important;
    color: $base !important;
    border: 0.06rem solid $base !important;
  }
  .btn-outline-#{$template}:not(.btn:disabled):hover {
    background-color: $main-background-color !important;
    color: $font-dark !important;
    border: 0.06rem solid $dark !important;
  }
  .btn-flat-#{$template} {
    background-color: transparent !important;
    color: $base !important;
    border: 0.06rem solid transparent !important;
    box-shadow: none !important;
  }
  .btn-flat-#{$template}:not(.btn:disabled):hover {
    background-color: $main-background-color !important;
    color: $font-dark !important;
    border: 0.06rem solid transparent !important;
  }
}

.btn {
  font-size: .9rem;
  box-shadow: none !important;
  border-radius: 5px;

  &.background-hover-trasparent:not(.btn:disabled):hover {
      background-color: transparent !important;
  }

  &:focus {
    box-shadow: 0 0 0 3px $main-background-color !important;
    background-color: $main-background-color !important;
  }
}

.btn:disabled {
  color: $dark-grey !important;

  &:not([class*=btn-flat]) {
    border-color: $light-grey !important;
    background-color: $light-grey !important;
  }
}

.btn-badge {
  position: absolute;
  color: white;
  padding: 1px 5px;
  border-radius: 50%;
  font-size: 0.7rem;
  top: -8px;
  left: -5px;

  &.badge-red {
    background: $red-base;
  }

  &.badge-yellow {
    background: $yellow-base;
  }

  &.badge-green {
    background: $green-base;
  }

  &.badge-blue {
    background-color: $blue-base;
  }
}

.btn-content {
  position: relative;

  &.icon-position-left {
    flex-direction: row;
  }

  &.icon-position-right {
    flex-direction: row-reverse;
  }

  .btn-loading-container {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-size: .45rem;
  }

  .btn-loading-container + * {
    color: transparent;
  }
}

.mlui-btn-loading {
  font-size: .45rem;
}

.align-to-left-baseline {
    margin-left: -0.75rem !important;
}

</style>
